import PropTypes from "prop-types";

import { useSettingsDirectoryPrivileges } from "@/account/withSettingsDirectoryPrivileges";
import DynamicBreadcrumbs from "@/components/DynamicBreadcrumbs";
import Sidebar from "@/components/Sidebar";
import {
  ArchivedSidebarStatus,
  ArchivedBadgeStatus,
} from "@/directory/DirectoryPageStatus";
import useGetUserExperimentSegment from "@/features/useGetUserExperimentSegment";
import useBreadcrumbLinks from "@/settings/useBreadcrumbsLinks";
import useSidebarMenuOptions from "@/settings/useSidebarMenuOptions";
import useUser from "@/users/useUser";
import {
  mainContainerCss,
  contentContainerCss,
  headerContainerCss,
  breadcrumbsCss,
  headerCss,
  titleCss,
  descriptionPageCss,
  separatorCss,
  containerCss,
  containerPaddingTopModifierCss,
  alertContainerCss,
  statusContainerCss,
} from "./SettingsBasePage.style";
import menuOptions, {
  breadcrumbOptions,
  ACCOUNT_OPTIONS,
} from "./SettingsBasePage.constants";

const propTypes = {
  /**
   * Optional node or array of nodes that will render in the body section.
   */
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  /**
   * Optional boolean indicating whether or not to display the separator line between the header and the content.
   */
  showHeaderSeparator: PropTypes.bool,
  /**
   * Optional boolean that indicates if the left strip bar is not to be displayed.
   */
  showSidebar: PropTypes.bool,
  /**
   * Optional boolean that indicates if the header is not to be displayed.
   */
  hiddenHeader: PropTypes.bool,
  /**
   * The title of the settings page.
   */
  title: PropTypes.string,
  /**
   * The sub menu item that will be added to the sidebar when the user is in a record page.
   */
  subMenuRecordInfo: PropTypes.shape({
    ariaLabel: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    content: PropTypes.node.isRequired,
    title: PropTypes.string.isRequired,
  }),

  /**
   * Optional node with different actions buttons.
   */
  actions: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  /**
   * The combinations of links and sub-section links that will be displayed in the breadcrumb.
   */
  breadcrumbsLinks: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  /**
   * The optional page description that goes below the page title.
   */
  pageDescription: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),

  /**
   * The optional Header Alert Component
   */
  AlertComponent: PropTypes.node,

  /**
   * Optional Badge Status Component
   */
  BadgeStatusComponent: PropTypes.node,
};

const SettingsBasePage = ({
  actions,
  title,
  children,
  showHeaderSeparator = false,
  showSidebar = true,
  hiddenHeader = false,
  subMenuRecordInfo = null,
  pageDescription = null,
  breadcrumbsLinks,
  AlertComponent,
  BadgeStatusComponent,
}) => {
  const { data: user } = useUser();
  const hasDirectoryView = useSettingsDirectoryPrivileges(user);
  const isCompleteView = hasDirectoryView && showSidebar;
  const [accessFacultyDirectoryViaSettingsPageExperiment] =
    useGetUserExperimentSegment("access_faculty_directory_via_settings_page");

  const inExperimentToAccessFacultyDirectoryViaTheSettingsPage =
    accessFacultyDirectoryViaSettingsPageExperiment === "on";

  let validBreadcrumbOptions = breadcrumbOptions;

  // Remove the Faculty-directory menu-item from the Settings page
  // if the user is not in this experiment.
  if (!inExperimentToAccessFacultyDirectoryViaTheSettingsPage) {
    const settingsPageMenuOptions = menuOptions.find(
      (option) => option.title === "Settings",
    );
    const validOptions = settingsPageMenuOptions.options.filter((option) => {
      return option.id !== ACCOUNT_OPTIONS.FACULTY.id;
    });

    settingsPageMenuOptions.options = validOptions;

    // Also, hide the "Faculty Directory" menu-item in the breadcrumb's
    // drop-down menu as well.
    validBreadcrumbOptions = breadcrumbOptions.filter((option) => {
      return option.href !== ACCOUNT_OPTIONS.FACULTY.url;
    });
  }

  const displayDirectoryLinks = isCompleteView ? validBreadcrumbOptions : [];
  const sideBarData = useSidebarMenuOptions(menuOptions, subMenuRecordInfo);
  const links = useBreadcrumbLinks(displayDirectoryLinks, breadcrumbsLinks);

  return (
    <div css={mainContainerCss}>
      {isCompleteView && <Sidebar {...sideBarData} />}
      <div css={contentContainerCss}>
        {AlertComponent && <div css={alertContainerCss}>{AlertComponent}</div>}
        {!hiddenHeader && (
          <div css={headerContainerCss}>
            <DynamicBreadcrumbs
              breadcrumbsCss={breadcrumbsCss}
              breadcrumbsLinks={links}
            />
            {BadgeStatusComponent && (
              <div css={statusContainerCss}>{BadgeStatusComponent}</div>
            )}
            <div css={headerCss}>
              {title && <h1 css={titleCss}>{title}</h1>}
              {!pageDescription ? actions : null}
            </div>
            {pageDescription && (
              <div data-testid="page-description" css={descriptionPageCss}>
                {pageDescription}
                {actions}
              </div>
            )}
          </div>
        )}
        {showHeaderSeparator && (
          <span data-testid="separator" css={separatorCss} />
        )}
        <div
          css={
            showHeaderSeparator
              ? containerCss
              : [containerCss, containerPaddingTopModifierCss]
          }
        >
          {children}
        </div>
      </div>
    </div>
  );
};

SettingsBasePage.propTypes = propTypes;

SettingsBasePage.ArchivedSidebarStatus = ArchivedSidebarStatus;
SettingsBasePage.ArchivedBadgeStatus = ArchivedBadgeStatus;

export default SettingsBasePage;
